import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import PlayerCard from "../../components/VirtualWorld/PlayerCard";
import SponsorMark, { JerseyPreview } from "../../components/VirtualWorld/SponsorMark";
import Loading from "../../components/Loading";

const ManagerTeam = () => {
  const { userId, manager, loading, refresh } = useVirtualWorld();
  const [selected, setSelected] = useState([]);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!userId || !manager?.club) return undefined;
    if (!manager.club.metTeamAt) {
      vw.markMetTeam(userId).then(() => refresh()).catch(() => {});
    }
    return undefined;
  }, [userId, manager?.club?.id, manager?.club?.metTeamAt, refresh]);

  // Nach Speichern / Server-Refresh lokale Auswahl an die gespeicherte Starting Four anbinden
  const serverStarterKey = (manager?.club?.starterKeys || []).join("|");
  useEffect(() => {
    setSelected([]);
  }, [serverStarterKey]);

  if (loading && !manager) return <Loading />;
  if (!manager?.club) return <p>Bitte zuerst einen Club gründen.</p>;

  const guided = Boolean(manager.guided);
  const players = manager.players || [];
  const playable = players.filter((p) => !p.loanedOut);
  const starterKeys = selected.length ? selected : manager.club.starterKeys || [];
  const starterSet = new Set(starterKeys);

  const starterPlayers = starterKeys
    .map((k) => players.find((p) => p.playerKey === k))
    .filter(Boolean);
  const reservePlayers = players.filter((p) => !p.loanedOut && !starterSet.has(p.playerKey));
  const loanedPlayers = players.filter((p) => p.loanedOut);

  const seasonNumber = manager.seasonNumber || 1;
  const budget = Number(manager.club.budget || 0);
  const canReleaseBase = playable.length > 4;
  const showAdmin = !guided || Boolean(manager.club.friendlyPlayedAt) || Number(manager.club.played || 0) >= 1;

  const remainingSeasons = (p) => Math.max(1, (p.contractEnd || seasonNumber) - seasonNumber + 1);
  const severance = (p) => {
    const raw = Math.round((p.wage || 0) * 4 * remainingSeasons(p));
    const isNpc = !p.isReal && !p.userId;
    const listedSale = Boolean(p.listed && p.listKind !== "loan");
    const squadFullNow = playable.length >= 6;
    if (isNpc && (squadFullNow || listedSale)) return 0;
    if (listedSale && squadFullNow) return Math.min(raw, Math.round(raw * 0.25));
    return raw;
  };
  const canAffordRelease = (p) => budget - severance(p) >= -10000;

  const toggle = (key) => {
    const player = players.find((p) => p.playerKey === key);
    if (player?.injuredOut) {
      setMsg(`${player.name} fällt verletzt aus und kann nicht aufgestellt werden.`);
      return;
    }
    setSelected((prev) => {
      const base = prev.length ? prev : manager.club.starterKeys || [];
      if (base.includes(key)) return base.filter((k) => k !== key);
      if (base.length >= 4) return base;
      return [...base, key];
    });
    setMsg("");
  };

  const save = async () => {
    setMsg("");
    try {
      await vw.setLineup(userId, starterKeys);
      await refresh();
      setMsg("Standard-Vier gespeichert. Vor dem Anpfiff setzt du H1–H4 auf dem Spielberichtsbogen.");
    } catch (err) {
      setMsg(err?.data?.message || "Fehler");
    }
  };

  const release = async (player) => {
    if (!canReleaseBase) {
      setMsg("Mindestens 4 Spieler müssen im Kader bleiben.");
      return;
    }
    if (!canAffordRelease(player)) {
      setMsg(
        `Nicht genug Budget für die Abfindung (${severance(player).toLocaleString("de-DE")} €). Spieler verkaufen oder verleihen.`
      );
      return;
    }
    if (
      !window.confirm(
        `${player.name} freistellen?\nAbfindung ca. ${severance(player).toLocaleString("de-DE")} €.\nDanach ist der Kaderplatz sofort frei.`
      )
    ) {
      return;
    }
    setMsg("");
    try {
      await vw.releasePlayer(userId, player.playerKey);
      setSelected([]);
      await refresh();
      setMsg(`${player.name} ist frei. Neuen Spieler holst du über den Transfermarkt.`);
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Kündigung fehlgeschlagen.");
    }
  };

  const renew = async (player) => {
    setMsg("");
    try {
      await vw.renewContract(userId, player.playerKey, 1);
      await refresh();
      setMsg(`${player.name}: Vertrag um 1 Saison verlängert, Gehalt +10 %.`);
    } catch (err) {
      setMsg(err?.data?.message || "Verlängerung fehlgeschlagen.");
    }
  };

  const adminActions = (p) =>
    showAdmin && !p.loanedOut ? (
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => renew(p)} className="px-3 py-1 rounded bg-gray-200">
          +1 Saison
        </button>
        <button
          type="button"
          disabled={!canReleaseBase || !canAffordRelease(p)}
          onClick={() => release(p)}
          title={
            !canReleaseBase
              ? "Mindestens 4 Spieler müssen bleiben"
              : !canAffordRelease(p)
                ? `Abfindung ca. ${severance(p).toLocaleString("de-DE")} € — Budget reicht nicht`
                : severance(p) === 0
                  ? "Sofort freistellen (keine Abfindung)"
                  : `Freistellen · Abfindung ca. ${severance(p).toLocaleString("de-DE")} €`
          }
          className="px-3 py-1 rounded bg-red-100 text-red-800 disabled:opacity-40"
        >
          {severance(p) === 0 ? "Freistellen" : "Kündigen"}
        </button>
        <Link className="px-3 py-1 rounded bg-amber-700 text-amber-50" to="/manager/transfers">
          Verleihen / Verkauf
        </Link>
        <Link className="px-3 py-1 rounded bg-gray-900 text-white" to="/manager/transfers">
          Tausch-Markt
        </Link>
      </div>
    ) : null;

  const renderCard = (p, { selected: isStarter, slot, reserve }) => (
    <PlayerCard
      key={p.playerKey}
      player={p}
      story={guided}
      compact={!guided}
      selected={!guided && isStarter}
      lineupFull={!guided && starterKeys.length >= 4}
      lineupSlot={slot}
      reserve={reserve}
      onSelect={p.loanedOut || p.injuredOut || guided ? undefined : () => toggle(p.playerKey)}
      actions={adminActions(p)}
    />
  );

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">
              {guided ? "Schritt · Mannschaft kennenlernen" : "Kader"}
            </div>
            <h2 className="text-3xl font-black mb-2 flex items-center gap-3 flex-wrap">
              {manager.club.sponsor?.brand ? <SponsorMark brand={manager.club.sponsor.brand} size={36} /> : null}
              <span>{manager.club.displayName || manager.club.name}</span>
            </h2>
            <p className="text-sm opacity-70 mb-2">Deine {playable.length} Spieler</p>
            <p className="opacity-80 mb-3">
              {guided
                ? "Jeder hat ein Gesicht und eine kurze Geschichte. Die Aufstellung H1–H4 machst du direkt auf dem Spielberichtsbogen vor dem Anpfiff."
                : "Oben die Starting Four, darunter die Reserve. Die Reihenfolge H1–H4 setzt du vor jedem Spiel auf dem Bogen."}
            </p>
            {showAdmin ? (
              <p className="text-sm opacity-70 mb-3">
                Platz schaffen: <strong>Kündigen</strong> (NPCs bei vollem Kader / gelistet oft ohne Abfindung) oder unter
                Transfers <strong>zum Verkauf listen</strong> (dann zählt der Slot für Käufe als frei) / Verleihen / Tausch.
              </p>
            ) : null}
            {guided ? (
              <Link to="/manager/match" className="inline-flex px-5 py-3 rounded-xl bg-amber-700 text-amber-50 font-semibold">
                Weiter zum Freundschaftsspiel
              </Link>
            ) : null}
          </div>
          {manager.club.sponsor?.brand &&
          manager.club.sponsorPlacement &&
          manager.club.sponsorPlacement !== "board" ? (
            <JerseyPreview brand={manager.club.sponsor.brand} placement={manager.club.sponsorPlacement} />
          ) : null}
        </div>
      </div>

      {!guided ? (
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <p className="text-sm opacity-80">
            Standard-Vier: {starterKeys.length}/4
            {starterKeys.length >= 4 ? " · voll — erst jemanden rausnehmen" : ""}
          </p>
          <button
            type="button"
            onClick={save}
            className="px-4 py-2 bg-green-500 text-white border border-yellow-400 rounded-xl font-semibold"
          >
            Standard-Vier speichern
          </button>
        </div>
      ) : null}

      {!guided && starterKeys.length < 4 ? (
        <div className="rounded-2xl border border-amber-400 bg-amber-50 dark:bg-amber-950/40 px-4 py-3">
          <p className="font-bold text-amber-950 dark:text-amber-100">
            Starting Four unvollständig ({starterKeys.length}/4)
          </p>
          <p className="text-sm opacity-90 mt-1">
            Du brauchst <strong>4 aufgestellte Starter</strong> (nicht nur 4 im Kader), damit
            Wettbewerbe in der Navigation erscheinen und Spiele angepfiffen werden können.
            Tippe bei einem freien Spieler auf „Aufstellen“, dann „Standard-Vier speichern“.
          </p>
        </div>
      ) : null}

      {msg ? <p className="font-semibold">{msg}</p> : null}

      {!guided ? (
        <>
          <section className="space-y-3">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-xl font-black">⭐ Starting Four</h3>
                <p className="text-sm opacity-70">Diese vier stehen oben — Tippen aktualisiert die Liste sofort.</p>
              </div>
              <span className="text-sm font-semibold tabular-nums opacity-80">{starterPlayers.length}/4</span>
            </div>
            <div className="rounded-3xl border-2 border-amber-400/80 bg-gradient-to-b from-amber-100/80 to-transparent dark:from-amber-950/30 p-3 md:p-4">
              {starterPlayers.length ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {starterPlayers.map((p, i) =>
                    renderCard(p, { selected: true, slot: `S${i + 1}`, reserve: false })
                  )}
                </div>
              ) : (
                <p className="text-sm opacity-70 px-1 py-6 text-center">
                  Noch niemand aufgestellt — wähle unten aus der Reserve.
                </p>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <div>
              <h3 className="text-xl font-black text-gray-700 dark:text-gray-200">Reserve</h3>
              <p className="text-sm opacity-70">Bank / Rotation — hier tippen, um in die Starting Four zu holen.</p>
            </div>
            <div className="rounded-3xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-100/50 dark:bg-black/20 p-3 md:p-4">
              {reservePlayers.length ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {reservePlayers.map((p) =>
                    renderCard(p, { selected: false, slot: "", reserve: true })
                  )}
                </div>
              ) : (
                <p className="text-sm opacity-70 px-1 py-4 text-center">Keine Reserve — alle Spieler sind Starter.</p>
              )}
            </div>
          </section>

          {loanedPlayers.length ? (
            <section className="space-y-3">
              <h3 className="text-lg font-bold opacity-80">Ausgeliehen</h3>
              <div className="grid md:grid-cols-2 gap-4 opacity-80">
                {loanedPlayers.map((p) => renderCard(p, { selected: false, slot: "", reserve: true }))}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {players.map((p) => renderCard(p, { selected: false, slot: "", reserve: false }))}
        </div>
      )}
    </div>
  );
};

export default ManagerTeam;
